/**
 * Listings Governance Page
 * Required fields, verification policies, written-off rules
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  ArrowLeft, Shield, Save, FileCheck, UserCheck,
  AlertTriangle, Car, CheckCircle, XCircle
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ListingsGovernancePage = () => {
  const [activeTab, setActiveTab] = useState('required-fields');
  const [selectedVehicleType, setSelectedVehicleType] = useState('cars');
  const [selectedCountry, setSelectedCountry] = useState('SL');
  const [saving, setSaving] = useState(false);
  
  // Required fields state
  const [requiredFields, setRequiredFields] = useState({
    cars: {
      always: ['make', 'model', 'year', 'price', 'mileage', 'fuelType', 'transmission'],
      recommended: ['colour', 'engineSize', 'bodyType', 'doors', 'description'],
      optional: ['vin', 'registrationNumber', 'serviceHistory']
    },
    'motorcycles-scooters': {
      always: ['make', 'model', 'year', 'price', 'engineCC'],
      recommended: ['colour', 'bikeType', 'description'],
      optional: ['mileage', 'abs']
    },
    'three-wheelers': {
      always: ['brand', 'year', 'price', 'fuelType'],
      recommended: ['configuration', 'engineCC', 'description'],
      optional: ['permitType', 'mileage']
    }
  });

  // Verification policies
  const [verificationPolicies, setVerificationPolicies] = useState({
    idVerification: {
      enabled: true,
      requiredFor: ['all'],
      documents: ['nationalId', 'passport', 'drivingLicense']
    },
    dealerVerification: {
      enabled: true,
      requiredDocuments: ['businessRegistration', 'dealerLicense'],
      verificationLevel: 'manual'
    },
    phoneVerification: {
      enabled: true,
      method: 'otp'
    },
    emailVerification: {
      enabled: true,
      method: 'link'
    }
  });

  // Written-off rules
  const [writtenOffRules, setWrittenOffRules] = useState({
    requireDisclosure: true,
    categories: [
      { code: 'A', label: 'Category A - Scrap Only', allowed: false },
      { code: 'B', label: 'Category B - Break for Parts', allowed: false },
      { code: 'S', label: 'Category S - Structural Damage', allowed: true, requiresInspection: true },
      { code: 'N', label: 'Category N - Non-Structural', allowed: true, requiresInspection: false }
    ],
    inspectionRequired: true,
    warningDisplay: 'prominent'
  });

  // Duplicate/spam thresholds
  const [spamThresholds, setSpamThresholds] = useState({
    maxListingsPerDay: 10,
    maxListingsPerWeek: 30,
    duplicateDetection: true,
    similarityThreshold: 85,
    minPhotos: 3,
    minDescriptionLength: 50,
    blockVpn: false,
    requireUniquePhone: true
  });

  const vehicleTypes = [
    { slug: 'cars', name: 'Cars', icon: '🚗' },
    { slug: 'vans', name: 'Vans', icon: '🚐' },
    { slug: 'buses', name: 'Buses', icon: '🚌' },
    { slug: 'motorcycles-scooters', name: 'Motorcycles', icon: '🏍️' },
    { slug: 'trucks-lorries', name: 'Trucks', icon: '🚚' },
    { slug: 'three-wheelers', name: 'Three-Wheelers', icon: '🛺' },
    { slug: 'boats-watercraft', name: 'Boats', icon: '🚤' }
  ];

  const allFields = [
    'make', 'model', 'year', 'price', 'mileage', 'fuelType', 'transmission',
    'colour', 'engineSize', 'bodyType', 'doors', 'seats', 'description',
    'vin', 'registrationNumber', 'serviceHistory', 'engineCC', 'bikeType',
    'brand', 'configuration', 'permitType', 'abs', 'condition'
  ];

  const saveChanges = async () => {
    setSaving(true);
    try {
      // Save to backend
      await axios.post(`${API_URL}/api/admin/vehicles/governance?admin_id=admin`, {
        required_fields: requiredFields,
        verification_policies: verificationPolicies,
        written_off_rules: writtenOffRules,
        spam_thresholds: spamThresholds,
        country: selectedCountry
      });
      alert('Governance settings saved!');
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Settings saved locally (API endpoint pending)');
    } finally {
      setSaving(false);
    }
  };

  const toggleFieldRequirement = (field, level) => {
    setRequiredFields(prev => {
      const current = prev[selectedVehicleType] || { always: [], recommended: [], optional: [] };
      const newFields = { ...current };
      
      // Remove from all levels first
      ['always', 'recommended', 'optional'].forEach(l => {
        newFields[l] = newFields[l].filter(f => f !== field);
      });
      
      // Add to new level if not 'none'
      if (level !== 'none') {
        newFields[level] = [...newFields[level], field];
      }
      
      return { ...prev, [selectedVehicleType]: newFields };
    });
  };

  const getFieldLevel = (field) => {
    const current = requiredFields[selectedVehicleType] || { always: [], recommended: [], optional: [] };
    if (current.always?.includes(field)) return 'always';
    if (current.recommended?.includes(field)) return 'recommended';
    if (current.optional?.includes(field)) return 'optional';
    return 'none';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin/vehicles">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="h-6 w-6 text-blue-600" />
              Listings Governance
            </h1>
            <p className="text-gray-500">Required fields, verification, spam prevention</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Select value={selectedCountry} onValueChange={setSelectedCountry}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="SL">🇱🇰 Sri Lanka</SelectItem>
              <SelectItem value="UK">🇬🇧 UK</SelectItem>
              <SelectItem value="EU">🇪🇺 EU</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={saveChanges} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="required-fields">
            <FileCheck className="h-4 w-4 mr-2" />
            Required Fields
          </TabsTrigger>
          <TabsTrigger value="verification">
            <UserCheck className="h-4 w-4 mr-2" />
            Verification
          </TabsTrigger>
          <TabsTrigger value="written-off">
            <AlertTriangle className="h-4 w-4 mr-2" />
            Written-Off
          </TabsTrigger>
          <TabsTrigger value="spam">
            <Shield className="h-4 w-4 mr-2" />
            Spam Prevention
          </TabsTrigger>
        </TabsList>

        {/* Required Fields Tab */}
        <TabsContent value="required-fields" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Required Fields by Vehicle Type</CardTitle>
                <Select value={selectedVehicleType} onValueChange={setSelectedVehicleType}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicleTypes.map(vt => (
                      <SelectItem key={vt.slug} value={vt.slug}>
                        {vt.icon} {vt.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {allFields.map(field => (
                  <div key={field} className="flex items-center justify-between p-3 border rounded-lg">
                    <span className="font-medium capitalize">{field.replace(/([A-Z])/g, ' $1')}</span>
                    <Select value={getFieldLevel(field)} onValueChange={(v) => toggleFieldRequirement(field, v)}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="always">
                          <span className="flex items-center gap-1 text-red-600">
                            <CheckCircle className="h-3 w-3" /> Required
                          </span>
                        </SelectItem>
                        <SelectItem value="recommended">
                          <span className="flex items-center gap-1 text-yellow-600">
                            <CheckCircle className="h-3 w-3" /> Recommended
                          </span>
                        </SelectItem>
                        <SelectItem value="optional">
                          <span className="flex items-center gap-1 text-gray-600">
                            <CheckCircle className="h-3 w-3" /> Optional
                          </span>
                        </SelectItem>
                        <SelectItem value="none">
                          <span className="flex items-center gap-1 text-gray-400">
                            <XCircle className="h-3 w-3" /> Hidden
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold mb-2">Legend</h4>
                <div className="flex flex-wrap gap-4 text-sm">
                  <span className="flex items-center gap-1 text-red-600"><CheckCircle className="h-4 w-4" /> Required - Must be filled</span>
                  <span className="flex items-center gap-1 text-yellow-600"><CheckCircle className="h-4 w-4" /> Recommended - Prompted but optional</span>
                  <span className="flex items-center gap-1 text-gray-600"><CheckCircle className="h-4 w-4" /> Optional - Shown but not prompted</span>
                  <span className="flex items-center gap-1 text-gray-400"><XCircle className="h-4 w-4" /> Hidden - Not shown in form</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Verification Tab */}
        <TabsContent value="verification" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>ID Verification</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Enable ID Verification</Label>
                  <Switch
                    checked={verificationPolicies.idVerification.enabled}
                    onCheckedChange={(v) => setVerificationPolicies(p => ({
                      ...p,
                      idVerification: { ...p.idVerification, enabled: v }
                    }))}
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Accepted Documents</Label>
                  <div className="space-y-2">
                    {['National ID', 'Passport', 'Driving License'].map(doc => (
                      <div key={doc} className="flex items-center gap-2">
                        <input type="checkbox" defaultChecked className="rounded" />
                        <span>{doc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Dealer Verification</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Enable Dealer Verification</Label>
                  <Switch
                    checked={verificationPolicies.dealerVerification.enabled}
                    onCheckedChange={(v) => setVerificationPolicies(p => ({
                      ...p,
                      dealerVerification: { ...p.dealerVerification, enabled: v }
                    }))}
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Required Documents</Label>
                  <div className="space-y-2">
                    {['Business Registration', 'Dealer License', 'Tax Certificate'].map(doc => (
                      <div key={doc} className="flex items-center gap-2">
                        <input type="checkbox" defaultChecked className="rounded" />
                        <span>{doc}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Verification Level</Label>
                  <Select defaultValue="manual">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Automatic</SelectItem>
                      <SelectItem value="manual">Manual Review</SelectItem>
                      <SelectItem value="hybrid">Hybrid (Auto + Spot Check)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Phone Verification</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Enable Phone Verification</Label>
                  <Switch
                    checked={verificationPolicies.phoneVerification.enabled}
                    onCheckedChange={(v) => setVerificationPolicies(p => ({
                      ...p,
                      phoneVerification: { ...p.phoneVerification, enabled: v }
                    }))}
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Method</Label>
                  <Select defaultValue="otp">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="otp">SMS OTP</SelectItem>
                      <SelectItem value="call">Voice Call</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp OTP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Email Verification</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Enable Email Verification</Label>
                  <Switch
                    checked={verificationPolicies.emailVerification.enabled}
                    onCheckedChange={(v) => setVerificationPolicies(p => ({
                      ...p,
                      emailVerification: { ...p.emailVerification, enabled: v }
                    }))}
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Method</Label>
                  <Select defaultValue="link">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="link">Verification Link</SelectItem>
                      <SelectItem value="otp">Email OTP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Written-Off Tab */}
        <TabsContent value="written-off" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Written-Off Vehicle Rules</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">Require Disclosure</p>
                  <p className="text-sm text-gray-500">Sellers must disclose if vehicle was written off</p>
                </div>
                <Switch
                  checked={writtenOffRules.requireDisclosure}
                  onCheckedChange={(v) => setWrittenOffRules(p => ({ ...p, requireDisclosure: v }))}
                />
              </div>

              <div>
                <h4 className="font-semibold mb-4">Write-Off Categories</h4>
                <div className="space-y-3">
                  {writtenOffRules.categories.map((cat, idx) => (
                    <div key={cat.code} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">{cat.label}</p>
                        {cat.requiresInspection && (
                          <Badge variant="outline" className="mt-1">Inspection Required</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <Select 
                          value={cat.allowed ? 'allowed' : 'blocked'}
                          onValueChange={(v) => {
                            const newCats = [...writtenOffRules.categories];
                            newCats[idx].allowed = v === 'allowed';
                            setWrittenOffRules(p => ({ ...p, categories: newCats }));
                          }}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="allowed">✅ Allowed</SelectItem>
                            <SelectItem value="blocked">❌ Blocked</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">Require Professional Inspection</p>
                  <p className="text-sm text-gray-500">For Category S and N vehicles</p>
                </div>
                <Switch
                  checked={writtenOffRules.inspectionRequired}
                  onCheckedChange={(v) => setWrittenOffRules(p => ({ ...p, inspectionRequired: v }))}
                />
              </div>

              <div>
                <Label className="mb-2 block">Warning Display Style</Label>
                <Select 
                  value={writtenOffRules.warningDisplay}
                  onValueChange={(v) => setWrittenOffRules(p => ({ ...p, warningDisplay: v }))}
                >
                  <SelectTrigger className="w-64">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prominent">⚠️ Prominent Banner</SelectItem>
                    <SelectItem value="standard">📋 Standard Notice</SelectItem>
                    <SelectItem value="minimal">ℹ️ Minimal Badge</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Spam Prevention Tab */}
        <TabsContent value="spam" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Spam & Duplicate Prevention</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="mb-2 block">Max Listings Per Day</Label>
                  <Input
                    type="number"
                    value={spamThresholds.maxListingsPerDay}
                    onChange={(e) => setSpamThresholds(p => ({ ...p, maxListingsPerDay: parseInt(e.target.value) }))}
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Max Listings Per Week</Label>
                  <Input
                    type="number"
                    value={spamThresholds.maxListingsPerWeek}
                    onChange={(e) => setSpamThresholds(p => ({ ...p, maxListingsPerWeek: parseInt(e.target.value) }))}
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Minimum Photos Required</Label>
                  <Input
                    type="number"
                    value={spamThresholds.minPhotos}
                    onChange={(e) => setSpamThresholds(p => ({ ...p, minPhotos: parseInt(e.target.value) }))}
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Min Description Length (chars)</Label>
                  <Input
                    type="number"
                    value={spamThresholds.minDescriptionLength}
                    onChange={(e) => setSpamThresholds(p => ({ ...p, minDescriptionLength: parseInt(e.target.value) }))}
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Similarity Threshold (%)</Label>
                  <Input
                    type="number"
                    value={spamThresholds.similarityThreshold}
                    onChange={(e) => setSpamThresholds(p => ({ ...p, similarityThreshold: parseInt(e.target.value) }))}
                  />
                  <p className="text-xs text-gray-500 mt-1">Listings above this similarity % are flagged as duplicates</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Duplicate Detection</p>
                    <p className="text-sm text-gray-500">Auto-detect similar listings</p>
                  </div>
                  <Switch
                    checked={spamThresholds.duplicateDetection}
                    onCheckedChange={(v) => setSpamThresholds(p => ({ ...p, duplicateDetection: v }))}
                  />
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Require Unique Phone</p>
                    <p className="text-sm text-gray-500">One phone number per account</p>
                  </div>
                  <Switch
                    checked={spamThresholds.requireUniquePhone}
                    onCheckedChange={(v) => setSpamThresholds(p => ({ ...p, requireUniquePhone: v }))}
                  />
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Block VPN/Proxy</p>
                    <p className="text-sm text-gray-500">Prevent listings from VPN users</p>
                  </div>
                  <Switch
                    checked={spamThresholds.blockVpn}
                    onCheckedChange={(v) => setSpamThresholds(p => ({ ...p, blockVpn: v }))}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ListingsGovernancePage;
